import React, { useState, useCallback, useMemo } from 'react';

// Constants for better maintainability
const API_ENDPOINTS = {
  CONFIRM_BOOKING: "https://uragate.in/websites/webapi2/tour-package/opr/confirm-booking",
  CANCEL_BOOKING: "https://uragate.in/websites/webapi2/tour-package/opr/cancel-booking",
  REJECT_BOOKING: "https://uragate.in/websites/webapi2/tour-package/opr/reject-booking"
};

const BOOKING_STATUS = {
  ALL: 'all',
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  CANCELLED: 'cancelled',
  CANCEL_REQUESTED: 'cancel-requested',
  REJECTED: 'rejected'
};

const Bookings = ({
  bookings,
  bookingStats,
  loading,
  bookingError,
  loadBookings,
  viewBookingDetails,
  formatDate,
  getStatusBadgeClass,
  getAuthHeaders,
  getOperatorId
}) => {
  const [activeFilter, setActiveFilter] = useState(BOOKING_STATUS.ALL);
  
  // Memoized data transformations
  const sortedBookings = useMemo(() => {
    return [...bookings].sort((a, b) => 
      new Date(b.booking_date) - new Date(a.booking_date)
    );
  }, [bookings]);

  const filteredBookings = useMemo(() => {
    if (activeFilter === BOOKING_STATUS.ALL) {
      return sortedBookings;
    }
    
    return sortedBookings.filter(booking => {
      const status = booking.status?.toLowerCase() || '';
      return status === activeFilter;
    });
  }, [sortedBookings, activeFilter]);

  // Memoized event handlers
  const handleStatCardClick = useCallback((filter) => {
    setActiveFilter(filter);
  }, []);

  const handleClearFilter = useCallback(() => {
    setActiveFilter(BOOKING_STATUS.ALL);
  }, []);

  const handleRetryLoad = useCallback(() => {
    loadBookings();
  }, [loadBookings]);

  const handleViewDetails = useCallback((booking) => {
    viewBookingDetails(booking);
  }, [viewBookingDetails]);

  // API functions
  const confirmBooking = useCallback(async (booking) => {
    try {
      const operatorId = getOperatorId();
      const bookingId = booking.booking_id;
      
      if (!bookingId) {
        alert("Booking ID not found");
        return;
      }

      const requestBody = {
        booking_id: bookingId,
        operator_id: operatorId
      };

      const response = await fetch(API_ENDPOINTS.CONFIRM_BOOKING, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (response.status === 401) {
        alert("Session expired. Please login again.");
        return;
      }

      const result = await response.json();

      if (result.success) {
        alert("Booking confirmed successfully!");
        loadBookings();
        setActiveFilter(BOOKING_STATUS.ALL);
      } else {
        throw new Error(result.message || "Failed to confirm booking");
      }
    } catch (error) {
      alert("Failed to confirm booking: " + error.message);
    }
  }, [getOperatorId, getAuthHeaders, loadBookings]);

  const cancelBooking = useCallback(async (booking) => {
    try {
      const operatorId = getOperatorId();
      const bookingId = booking.booking_id;
      
      if (!bookingId) {
        alert("Booking ID not found");
        return;
      }

      const requestBody = {
        booking_id: bookingId,
        operator_id: operatorId
      };

      const response = await fetch(API_ENDPOINTS.CANCEL_BOOKING, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (response.status === 401) {
        alert("Session expired. Please login again.");
        return;
      }

      const result = await response.json();

      if (result.success) {
        alert("Booking cancelled successfully!");
        loadBookings();
        setActiveFilter(BOOKING_STATUS.ALL);
      } else {
        throw new Error(result.message || "Failed to cancel booking");
      }
    } catch (error) {
      alert("Failed to cancel booking: " + error.message);
    }
  }, [getOperatorId, getAuthHeaders, loadBookings]);

  // Add reject booking API function
  const rejectBooking = useCallback(async (booking, reason = "Booking rejected by operator") => {
    try {
      const operatorId = getOperatorId();
      const bookingId = booking.booking_id;
      
      if (!bookingId) {
        alert("Booking ID not found");
        return;
      }

      const requestBody = {
        booking_id: bookingId,
        operator_id: operatorId,
        reason: reason
      };

      const response = await fetch(API_ENDPOINTS.REJECT_BOOKING, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (response.status === 401) {
        alert("Session expired. Please login again.");
        return;
      }

      const result = await response.json();

      if (result.success) {
        alert("Booking rejected successfully!");
        loadBookings();
        setActiveFilter(BOOKING_STATUS.ALL);
      } else {
        throw new Error(result.message || "Failed to reject booking");
      }
    } catch (error) {
      alert("Failed to reject booking: " + error.message);
    }
  }, [getOperatorId, getAuthHeaders, loadBookings]);

  // Booking action handler
  const handleBookingAction = useCallback(async (booking, action) => {
    const messages = {
      confirm: 'Are you sure you want to confirm this booking?',
      cancel: 'Are you sure you want to approve this cancellation request?',
      reject: 'Are you sure you want to reject this booking?',
      rejectCancellation: 'Are you sure you want to reject this cancellation request?'
    };

    if (!window.confirm(messages[action] || `Are you sure you want to ${action} this booking?`)) {
      return;
    }

    try {
      if (action === 'confirm') {
        await confirmBooking(booking);
      } else if (action === 'cancel') {
        await cancelBooking(booking);
      } else if (action === 'reject' || action === 'rejectCancellation') {
        // For rejection, you might want to ask for a reason
        const reason = prompt("Please provide a reason for rejection:", "Booking rejected by operator");
        if (reason !== null) {
          await rejectBooking(booking, reason);
        }
      }
    } catch (error) {
      alert(`Failed to ${action} booking. Please try again.`);
    }
  }, [confirmBooking, cancelBooking, rejectBooking]);

  // Memoized booking card component
  const BookingCard = useCallback(({ booking }) => {
    const bookingId = booking.booking_id;
    const status = booking.status || 'Unknown';
    const statusLower = status.toLowerCase();
    const isPending = statusLower === BOOKING_STATUS.PENDING;
    const isCancelRequested = statusLower === BOOKING_STATUS.CANCEL_REQUESTED;

    return (
      <div className="booking-card">
        <div className="booking-header">
          <h3>Booking #{bookingId || 'N/A'}</h3>
          <span className={`status-badge ${getStatusBadgeClass(status)}`}>
            {status}
          </span>
        </div>

        <div className="booking-content">
          <div className="booking-info-grid">
            <InfoItem label="Package ID" value={booking.package_id} />
            <InfoItem label="User ID" value={booking.user_id} />
            <InfoItem label="Booking Date" value={formatDate(booking.booking_date)} />
            <InfoItem label="Travel Date" value={formatDate(booking.start_date)} />
            <InfoItem label="Number of Guests" value={booking.num_guests} />
            <AmountItem label="Total Amount" amount={booking.total_amount} />
            <AmountItem label="Amount Paid" amount={booking.amount_paid} defaultAmount="0.00" />
            <PaymentStatusItem status={booking.payment_status} />
          </div>

          {booking.special_requests && (
            <div className="special-requests">
              <label>Special Requests:</label>
              <p>{booking.special_requests}</p>
            </div>
          )}
        </div>

        <div className="booking-actions">
          {isPending && (
            <>
              <ActionButton 
                type="confirm"
                onClick={() => handleBookingAction(booking, 'confirm')}
                label="Confirm Booking"
              />
              <ActionButton 
                type="reject"
                onClick={() => handleBookingAction(booking, 'reject')}
                label="Reject Booking"
              />
            </>
          )}
          
          {isCancelRequested && (
            <>
              <ActionButton 
                type="cancel"
                onClick={() => handleBookingAction(booking, 'cancel')}
                label="Approve Cancellation"
              />
              <ActionButton 
                type="reject"
                onClick={() => handleBookingAction(booking, 'rejectCancellation')}
                label="Reject Cancellation"
              />
            </>
          )}
          
          <ActionButton 
            type="view"
            onClick={() => handleViewDetails(booking)}
            label="View Details"
          />
        </div>
      </div>
    );
  }, [getStatusBadgeClass, formatDate, handleBookingAction, handleViewDetails]);

  // Helper components
  const InfoItem = useCallback(({ label, value, fallback = 'Not specified' }) => (
    <div className="info-item">
      <label>{label}:</label>
      <span>{value || fallback}</span>
    </div>
  ), []);

  const AmountItem = useCallback(({ label, amount, defaultAmount = 'Not specified' }) => (
    <div className="info-item">
      <label>{label}:</label>
      <span className="booking-amount">
        {amount ? `₹${amount}` : `₹${defaultAmount}`}
      </span>
    </div>
  ), []);

  const PaymentStatusItem = useCallback(({ status }) => (
    <div className="info-item">
      <label>Payment Status:</label>
      <span className={`payment-status ${status || 'unpaid'}`}>
        {status || 'unpaid'}
      </span>
    </div>
  ), []);

  const ActionButton = useCallback(({ type, onClick, label }) => (
    <button 
      className={`btn-${type}`} 
      onClick={onClick}
      aria-label={`${label} for booking`}
    >
      {label}
    </button>
  ), []);

  // Memoized stats cards
  const StatCard = useCallback(({ type, count, label, isActive, onClick }) => (
    <div 
      className={`stat-card ${isActive ? 'active' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Filter by ${label}`}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick();
        }
      }}
    >
      <div className="stat-number">{count}</div>
      <div className="stat-label">{label}</div>
    </div>
  ), []);

  const statsConfig = useMemo(() => [
    { type: BOOKING_STATUS.ALL, count: bookingStats.total, label: 'Total Bookings' },
    { type: BOOKING_STATUS.PENDING, count: bookingStats.pending, label: 'Pending' },
    { type: BOOKING_STATUS.CONFIRMED, count: bookingStats.confirmed, label: 'Confirmed' },
    { type: BOOKING_STATUS.CANCELLED, count: bookingStats.cancelled, label: 'Cancelled' },
    { type: BOOKING_STATUS.CANCEL_REQUESTED, count: bookingStats['cancel-requested'] || 0, label: 'Cancel Requests' },
    { type: BOOKING_STATUS.REJECTED, count: bookingStats.rejected || 0, label: 'Rejected' }
  ], [bookingStats]);

  // Render functions
  const renderStatsCards = useMemo(() => (
    statsConfig.map((stat) => (
      <StatCard
        key={stat.type}
        type={stat.type}
        count={stat.count}
        label={stat.label}
        isActive={activeFilter === stat.type}
        onClick={() => handleStatCardClick(stat.type)}
      />
    ))
  ), [statsConfig, activeFilter, handleStatCardClick]);

  const renderBookingCards = useMemo(() => {
    if (loading.bookings) {
      return (
        <div className="loading-bookings" aria-live="polite">
          <div className="spinner"></div>
          <p>Loading bookings...</p>
        </div>
      );
    }

    if (bookingError) {
      return (
        <div className="bookings-error" aria-live="polite">
          <div className="error-icon">⚠️</div>
          <h3>Unable to Load Bookings</h3>
          <p>{bookingError}</p>
          <button onClick={handleRetryLoad} className="btn-retry">
            Try Again
          </button>
        </div>
      );
    }

    if (filteredBookings.length === 0) {
      const displayText = activeFilter === BOOKING_STATUS.ALL 
        ? { title: 'No Bookings Yet', message: "You don't have any bookings at the moment." }
        : { 
            title: `No ${activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)} Bookings`,
            message: `You don't have any ${activeFilter} bookings at the moment.`
          };

      return (
        <div className="no-bookings" aria-live="polite">
          <h3>{displayText.title}</h3>
          <p>{displayText.message}</p>
          {activeFilter !== BOOKING_STATUS.ALL && (
            <button onClick={handleClearFilter} className="btn-view-all">
              View All Bookings
            </button>
          )}
        </div>
      );
    }

    return filteredBookings.map((booking) => (
      <BookingCard key={booking.booking_id} booking={booking} />
    ));
  }, [
    loading.bookings,
    bookingError,
    filteredBookings,
    activeFilter,
    handleRetryLoad,
    handleClearFilter,
    BookingCard
  ]);

  return (
    <div className="bookings-section">
      <div className="bookings-header">
        <h2>Bookings Management</h2>
        <div className="filter-indicator">
          {activeFilter !== BOOKING_STATUS.ALL && (
            <div className="active-filter">
              Showing: <p style={{textDecoration:"underline"}}>{activeFilter}</p>
              <button 
                onClick={handleClearFilter} 
                className="btn-clear-filter"
                aria-label="Clear filter"
              >
                Clear Filter
              </button>
            </div>
          )}
        </div>
        <div className="bookings-stats">
          {renderStatsCards}
        </div>
      </div>

      <div className="bookings-container">
        {renderBookingCards}
      </div>
    </div>
  );
};

export default React.memo(Bookings);